import styled from 'styled-components'
import { theme } from '../../theme/theme'
import { BodySmall } from '../styles/Typography'
import { SEGMENT_STACK_THICKNESS, SegmentStack, sumBySteps } from './SegmentStack'
import { ChartStep, SegmentRow } from './type'

// `wide` fits full condition names, `narrow` the short severity-tier labels.
const LABEL_WIDTH = { narrow: 144, wide: 200 } as const
// Totals get their own column so they line up down the right edge.
const TOTAL_WIDTH = 40

type Props = {
  steps: readonly ChartStep[]
  rows: readonly SegmentRow[]
  /** Unit for the tooltip, e.g. "kvinnor" or "%". */
  unit: string
  labelColumn?: keyof typeof LABEL_WIDTH
  /** Scales bars against the largest row's total and shows it beside each bar, replacing the
   * inline segment numbers. */
  showRowTotals?: boolean
  /** Appends the unit to the inline segment label too, e.g. "91%" instead of "91". */
  showUnitInline?: boolean
}

export const SegmentRowChart = ({
  steps,
  rows,
  unit,
  labelColumn = 'narrow',
  showRowTotals = false,
  showUnitInline = false,
}: Props) => {
  const rowsWithTotal = rows.map((row) => ({ ...row, total: sumBySteps(steps, row.values) }))
  const maxTotal = Math.max(...rowsWithTotal.map(({ total }) => total), 0)

  return (
    <RowList>
      {rowsWithTotal.map(({ key, label, values, total, scaleTotal }) => (
        <Row key={key} $labelWidth={LABEL_WIDTH[labelColumn]} $showTotal={showRowTotals}>
          <RowLabel>{label}</RowLabel>
          {total > 0 ? (
            <SegmentStack
              steps={steps}
              values={values}
              unit={unit}
              widthPercent={showRowTotals ? (total / maxTotal) * 100 : 100}
              showInlineValues={!showRowTotals}
              showUnitInline={showUnitInline}
              scaleTotal={scaleTotal}
            />
          ) : (
            // An empty row still gets a track, so the shared scale stays readable.
            <EmptyBar />
          )}
          {showRowTotals && <RowTotal>{total}</RowTotal>}
        </Row>
      ))}
    </RowList>
  )
}

const RowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.small}px;
`

const Row = styled.div<{ $labelWidth: number; $showTotal: boolean }>`
  display: grid;
  grid-template-columns: ${({ $labelWidth, $showTotal }) =>
    `${$labelWidth}px 1fr${$showTotal ? ` ${TOTAL_WIDTH}px` : ''}`};
  align-items: center;
  gap: ${theme.spacing.small}px;

  /* Too narrow for a label column: the label moves above the bar. */
  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    grid-template-columns: ${({ $showTotal }) => `1fr${$showTotal ? ` ${TOTAL_WIDTH}px` : ''}`};
    gap: ${theme.spacing.tiny}px ${theme.spacing.xsmall}px;

    & > :first-child {
      grid-column: 1 / -1;
    }
  }
`

const RowLabel = styled(BodySmall)`
  color: ${theme.color.plum};
`

const RowTotal = styled(BodySmall)`
  color: ${theme.color.blueDark};
  font-variant-numeric: tabular-nums;
  text-align: right;
`

const EmptyBar = styled.div`
  height: ${SEGMENT_STACK_THICKNESS}px;
  background-color: ${theme.color.grey1};
  border-radius: 4px;
`
